import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X } from 'lucide-react';
import { useCurrency } from '../contexts/CurrencyContext';
import CurrencySelector from './CurrencySelector';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { formatPrice } = useCurrency();

  const isActive = (path) => location.pathname === path;

  // Scroll to top when clicking any navigation link
  const handleNavClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/features', label: 'Features' },
    { path: '/pricing', label: 'Pricing' },
    { path: '/industries', label: 'Industries' },
    { path: '/about', label: 'About' },
    { path: '/blog', label: 'Blog' },
    { path: '/contact', label: 'Contact' }
  ];

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <Link to="/" onClick={handleNavClick} className="flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_local-hero-site/artifacts/jgz9n22r_Local%20Business%20Heroes%20Logo%20v2.png" 
              alt="Local Business Heroes" 
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={handleNavClick}
                className={`text-sm font-medium transition-colors hover:text-[#FF5A3D] ${
                  isActive(link.path) ? 'text-[#FF5A3D]' : 'text-gray-700'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Currency Selector & CTA Buttons */}
          <div className="hidden lg:flex items-center space-x-3">
            <CurrencySelector />
            <Button 
              asChild
              variant="outline"
              className="border-2 border-[#FF5A3D] text-[#FF5A3D] hover:bg-[#FF5A3D]/10 font-semibold px-4 py-2 rounded-lg"
            >
              <a href="https://app.localbusinessheroes.net/" target="_blank" rel="noopener noreferrer">
                Login
              </a>
            </Button>
            <Button 
              asChild
              className="bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42] hover:from-[#E5512E] hover:to-[#E57A35] text-white font-semibold px-6 py-2 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              <Link to="/pricing">
                Start 14-Day Trial
              </Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t">
            <div className="flex flex-col space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium transition-colors hover:text-[#FF5A3D] ${
                    isActive(link.path) ? 'text-[#FF5A3D]' : 'text-gray-700'
                  }`}
                  onClick={handleNavClick}
                >
                  {link.label}
                </Link>
              ))}
              <div className="flex flex-col space-y-3">
                <CurrencySelector variant="mobile" />
                <Button 
                  asChild
                  variant="outline"
                  className="border-2 border-[#FF5A3D] text-[#FF5A3D] hover:bg-[#FF5A3D]/10 font-semibold px-4 py-2 rounded-lg w-full"
                >
                  <a href="https://app.localbusinessheroes.net/" target="_blank" rel="noopener noreferrer">
                    Login
                  </a>
                </Button>
                <Button 
                  asChild
                  className="bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42] hover:from-[#E5512E] hover:to-[#E57A35] text-white font-semibold px-6 py-2 rounded-lg transition-all duration-300 w-full"
                >
                  <Link to="/pricing" onClick={handleNavClick}>
                    Start 14-Day Trial
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;